import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';

import useTopPanel from 'hooks/useTopPanel';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useMenu from 'hooks/useMenu';
import { useQuery } from 'react-query';
import moment from 'moment';
import FormDateField from 'components/ui/form/FormDateField';
import FormSimpleSelectField from 'components/ui/form/FormSimpleSelectField';
import { useFormik } from 'formik';
import ContractTasksTab from 'pages/admin/contract/Tasks/ContractTasksTab';
import DebtTaskSettingsModal from './DebtTaskSettingsModal';

const StaffTasks = () => {
  const { setComponent } = useTopPanel();
  const { t, i18n } = useTranslation();
  const { hasChild } = useMenu();
  const axiosPrivate = useAxiosPrivate();
  const [hasError, setHasError] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const canView = hasChild('STAFF_TASKS', 'STAFF_VIEW_TASKS');
  const canEdit = hasChild('STAFF_TASKS', 'STAFF_EDIT_TASKS');

  const formik = useFormik({
    initialValues: {
      from_date: moment().startOf('month').format('YYYY-MM-DD'),
      till_date: moment().endOf('month').format('YYYY-MM-DD'),
      user_id: '',
      status: '',
    },
  });

  const statusOptions = useMemo(
    () => [
      { id: '', name: t('common.filter.all') },
      { id: 1, name: t('tasks.staffTask.status.active') },
      { id: 2, name: t('tasks.staffTask.status.complete') },
      { id: 0, name: t('tasks.staffTask.status.inactive') },
    ],
    [i18n.language]
  );

  const { data: staffes } = useQuery({
    queryKey: ['/dictionary/staffes'],
    queryFn: async () => {
      const response = await axiosPrivate.get('/dictionary/staffes');
      return response.data.data || [];
    },
    enabled: !hasError && canView,
    retry: false,
  });

  const staffOptions = useMemo(
    () => [
      { id: '', name: t('common.filter.all') },
      ...(staffes || []),
    ],
    [staffes, i18n.language]
  );

  const { data: tasks, refetch } = useQuery({
    queryKey: ['admin/task-staff/index', formik.values],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (formik.values.from_date) params.set('from_date', formik.values.from_date);
      if (formik.values.till_date) params.set('till_date', formik.values.till_date);
      if (formik.values.user_id) params.set('user_id', formik.values.user_id);
      if (formik.values.status !== '' && formik.values.status != null)
        params.set('status', formik.values.status);
      const response = await axiosPrivate.get(
        `/admin/task-staff/index?${params.toString()}`
      );
      return response.data.data || [];
    },
    enabled: !hasError && canView,
    onError: () => setHasError(true),
    retry: false,
  });

  useEffect(() => {
    setComponent(
      <div className='component-title'>{t('sidebar.admin.staffTasks')}</div>
    );
  }, [i18n.language]);

  return (
    <div className='component-list-wrapper'>
      <div className='component-list-header mb-2'>
        <div className='header-actions-container py-3 flex flex-row items-center gap-4'>
          <div className='flex items-center gap-3 flex-1'>
            <div className='w-44'>
              <FormDateField
                delay={0}
                label={t('common.fields.fromDate')}
                fieldName='from_date'
                formik={formik}
              />
            </div>
            <div className='w-44'>
              <FormDateField
                delay={0}
                label={t('common.fields.endDate')}
                fieldName='till_date'
                formik={formik}
              />
            </div>
            <div className='w-44'>
              <FormSimpleSelectField
                delay={0}
                label={t('common.fields.staff')}
                fieldName='user_id'
                formik={formik}
                options={staffOptions}
                itemValue='id'
                itemLabel='name'
                size='small'
              />
            </div>
            <div className='w-44'>
              <FormSimpleSelectField
                delay={0}
                label={t('common.fields.status')}
                fieldName='status'
                formik={formik}
                options={statusOptions}
                itemValue='id'
                itemLabel='name'
                size='small'
              />
            </div>
          </div>
          {canEdit && (
            <Button
              variant='base'
              color='primary'
              onClick={() => setSettingsOpen(true)}
            >
              <i className='bi bi-gear mr-1' />
              {t('tasks.staffTask.debtTask.button')}
            </Button>
          )}
          <Button
            variant='base'
            color='info'
            onClick={() => refetch()}
          >
            <i className='bi bi-arrow-repeat' />
          </Button>
        </div>
      </div>

      <div className='component-table-wrapper'>
        <ContractTasksTab tasks={tasks || []} refetchFn={refetch} />
      </div>

      {canEdit && (
        <DebtTaskSettingsModal
          open={settingsOpen}
          setOpen={setSettingsOpen}
          refetchFn={refetch}
        />
      )}
    </div>
  );
};

export default memo(StaffTasks);
